import { DOCUMENT } from "@angular/common";
import { Inject, Injectable } from "@angular/core";
import { Router, UrlSerializer } from "@angular/router";
import { translateSafely } from "@ankaadia/ankaadia-shared-frontend";
import { UserEnvironment, UserHasSeenNewAnkaadiaAnnouncementGQL, UserPrefersNewUiGQL } from "@ankaadia/graphql";
import { isNil } from "lodash";
import { ConfirmationService as OldConfirmationService, PrimeIcons as OldPrimeIcons } from "primeng-v17/api";
import { ConfirmationService, PrimeIcons } from "primeng/api";
import { map, Observable } from "rxjs";
import { environment } from "../../../environments/environment";

@Injectable({ providedIn: "root" })
export class UserEnvironmentService {
  constructor(
    @Inject(DOCUMENT) private readonly documentRef: Document,
    @Inject(environment.newUI ? ConfirmationService : OldConfirmationService)
    private readonly confirmationService: ConfirmationService | OldConfirmationService,
    private readonly router: Router,
    private readonly urlSerializer: UrlSerializer,
    private readonly userHasSeenNewAnkaadiaAnnouncementGQL: UserHasSeenNewAnkaadiaAnnouncementGQL,
    private readonly userPrefersNewUiGQL: UserPrefersNewUiGQL
  ) {}

  userHasSeenNewAnkaadiaAnnouncement(): Observable<UserEnvironment> {
    return this.userHasSeenNewAnkaadiaAnnouncementGQL
      .mutate()
      .pipe(map((x) => x.data.userHasSeenNewAnkaadiaAnnouncement));
  }

  userPrefersNewUi(value: boolean): Observable<UserEnvironment> {
    return this.userPrefersNewUiGQL.mutate({ input: { value } }).pipe(map((x) => x.data.userPrefersNewUi));
  }

  navigateToAlternativeEnvironment(): void {
    const alternativeEnvironmentUrl = environment.alternativeEnvironmentUrl;
    if (isNil(alternativeEnvironmentUrl)) {
      return;
    }

    const url = this.getFullAlternativeUrl(alternativeEnvironmentUrl);

    this.documentRef.defaultView.location.href = url;
  }

  switchToAlternativeEnvironment(confirmationDialogKey: string, event: MouseEvent): void {
    const alternativeEnvironmentUrl = environment.alternativeEnvironmentUrl;
    if (isNil(alternativeEnvironmentUrl)) {
      return;
    }

    if (environment.newUI) {
      this.confirmationService.confirm({
        target: event.target,
        header: translateSafely("ankaadiaEnvironment.classic.confirmSwitch.header"),
        message: translateSafely("ankaadiaEnvironment.classic.confirmSwitch.message"),
        acceptLabel: translateSafely("ankaadiaEnvironment.classic.confirmSwitch.acceptLabel"),
        rejectLabel: translateSafely("common.cancel"),
        acceptButtonProps: { severity: "success" },
        rejectButtonStyleClass: "p-button-text",
        icon: PrimeIcons.EXCLAMATION_TRIANGLE,
        key: confirmationDialogKey,
        accept: () => this.userPrefersNewUi(false).subscribe(() => this.navigateToAlternativeEnvironment()),
      });
    } else {
      this.confirmationService.confirm({
        target: event.target,
        header: translateSafely("ankaadiaEnvironment.newUi.confirmSwitch.header"),
        message: translateSafely("ankaadiaEnvironment.newUi.confirmSwitch.message"),
        acceptLabel: translateSafely("ankaadiaEnvironment.newUi.confirmSwitch.acceptLabel"),
        rejectLabel: translateSafely("common.cancel"),
        icon: OldPrimeIcons.EXCLAMATION_TRIANGLE,
        key: confirmationDialogKey,
        accept: () => this.userPrefersNewUi(true).subscribe(() => this.navigateToAlternativeEnvironment()),
      });
    }
  }

  private getFullAlternativeUrl(baseUrl: string): string {
    const currentUrlTree = this.router.parseUrl(this.router.url);
    const serializedRoute = this.urlSerializer.serialize(currentUrlTree);

    const base = new URL(baseUrl);
    base.searchParams.append("clearLocalStorage", "true");
    return `${base.origin}${serializedRoute}${base.search}`;
  }
}
